import { Link, useLocation } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { toast } from 'react-toastify'
import { useAuth } from '../../contexts/AuthContext'
import { useRateLimit } from '../../hooks/useRateLimit'
import {
  Container,
  Content,
  MenuIcon,
  NavMenu,
  DesktopLinks,
  Logo,
  MenuOverlay,
  NavLink,
  DropdownItem,
  ThemeToggle,
  ThemeToggleMobile,
  BottomNav,
  BottomNavItem,
  SearchOverlay,
  SearchPanel,
  SearchHeader,
  SearchInput,
  SearchResults,
  SearchResultItem,
  SearchClose,
  AuthButtons,
  AuthButton,
  UserMenuContainer,
  UserAvatar,
  UserMenuButton,
  UserDropdown,
  UserDropdownHeader,
  UserDropdownItem,
  UsageBadge,
} from './styles'
import {
  FiMenu,
  FiX,
  FiMoon,
  FiSun,
  FiHome,
  FiGrid,
  FiDroplet,
  FiSearch,
  FiLogOut,
  FiChevronDown,
  FiSettings,
  FiCreditCard,
  FiShield,
} from 'react-icons/fi'

export function Header({ themeName = 'light', onToggleTheme }) {
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const location = useLocation()
  const isDark = themeName === 'dark'
  const { user, isAuthenticated, logout } = useAuth()
  const { rateLimit } = useRateLimit()
  const userMenuRef = useRef(null)

  const closeMenu = () => setIsOpen(false)
  const toggleUserMenu = () => setIsUserMenuOpen((prev) => !prev)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  useEffect(() => {
    setIsOpen(false)
    setIsSearchOpen(false)
    setIsUserMenuOpen(false)
  }, [location.pathname])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false)
      }
    }

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isUserMenuOpen])

  const handleLogout = async () => {
    try {
      await logout()
      toast.success('Logout realizado com sucesso!')
      setIsUserMenuOpen(false)
    } catch (error) {
      toast.error('Erro ao fazer logout')
    }
  }

  const handleManageSubscription = async () => {
    try {
      const { api } = await import('../../services/api')
      const { data } = await api.post('/stripe/create-portal-session')

      if (data.url) {
        window.location.href = data.url
      } else {
        toast.error('Erro ao abrir portal de gerenciamento')
      }
    } catch (error) {
      console.error('Error creating portal session:', error)
      toast.error('Erro ao abrir portal. Tente novamente.')
    }
    setIsUserMenuOpen(false)
  }

  const getBadgeVariant = () => {
    if (rateLimit.isLoading) return 'normal'
    const isActiveSubscription = user?.subscriptionStatus === 'active'
    if (isActiveSubscription || rateLimit.remaining === 'unlimited') return 'unlimited'
    if (rateLimit.remaining === 0) return 'danger'
    if (rateLimit.remaining <= 1) return 'warning'
    return 'normal'
  }

  const getBadgeText = () => {
    if (rateLimit.isLoading) return 'Carregando...'
    const isActiveSubscription = user?.subscriptionStatus === 'active'
    if (isActiveSubscription || rateLimit.remaining === 'unlimited') return 'Ilimitado ✓'
    return `${rateLimit.remaining}/${rateLimit.limit}`
  }

  const getUserInitials = () => {
    if (!user?.email) return '?'
    const emailPrefix = user.email.split('@')[0]
    return emailPrefix.substring(0, 2).toUpperCase()
  }

  useEffect(() => {
    const updateShellWidth = () => {
      const rootEl = document.getElementById('root')
      if (rootEl) {
        const width = `${rootEl.clientWidth}px`
        document.documentElement.style.setProperty('--app-shell-width', width)
      } else {
        document.documentElement.style.setProperty('--app-shell-width', '100vw')
      }
    }

    updateShellWidth()
    window.addEventListener('resize', updateShellWidth)
    window.addEventListener('orientationchange', updateShellWidth)

    return () => {
      window.removeEventListener('resize', updateShellWidth)
      window.removeEventListener('orientationchange', updateShellWidth)
    }
  }, [])

  const bottomItems = [
    { label: 'Home', icon: FiHome, to: '/' },
    { label: 'Estruturas', icon: FiGrid, to: '/concreto' },
    { label: 'Instalações', icon: FiDroplet, to: '/hidrossanitario' },
    { label: 'Buscar', icon: FiSearch, action: 'search' },
  ]

  const quickSearchItems = [
    { label: 'Flexão em Concreto', category: 'Concreto', path: '/concreto/flexao' },
    { label: 'Cisalhamento em Concreto', category: 'Concreto', path: '/concreto/cisalhamento' },
    { label: 'Torção em Concreto', category: 'Concreto', path: '/concreto/torcao' },
    { label: 'Consolo em Concreto', category: 'Concreto', path: '/concreto/consolo' },
    { label: 'Fundação de Parede', category: 'Concreto', path: '/concreto/fundacao-parede' },
    { label: 'Inércia Equivalente', category: 'Concreto', path: '/concreto/inercia-equivalente' },
    { label: 'Fadiga da Armadura Longitudinal', category: 'Concreto', path: '/concreto/fadiga-armadura-longitudinal' },
    { label: 'Fadiga da Armadura Transversal', category: 'Concreto', path: '/concreto/fadiga-armadura-transversal' },
    { label: 'Escada em Concreto', category: 'Concreto', path: '/concreto/escada-longitudinal-patamar' },
    { label: 'Sapata Isolada', category: 'Concreto', path: '/concreto/sapata-isolada' },
    { label: 'Momento por Área', category: 'Concreto', path: '/concreto/momento-area' },
    { label: 'Camada de Armadura', category: 'Concreto', path: '/concreto/camada-armadura' },
    { label: 'Flexo-Tração em Concreto', category: 'Concreto', path: '/concreto/flexo-tracao' },
    { label: 'Flexo-Compressão Oblíqua em Concreto', category: 'Concreto', path: '/concreto/flexo-compressao-obliqua' },
    { label: 'Dimensionamento de Pilares em Concreto', category: 'Concreto', path: '/concreto/pilares' },
    { label: 'Ancoragem em Concreto', category: 'Concreto', path: '/concreto/ancoragem' },
    { label: 'Pórtico 2D (Análise Matricial)', category: 'Concreto', path: '/concreto/portico-2d' },
    { label: 'Solda axial', category: 'Aço', path: '/aco/solda-axial' },
    { label: 'Placa de base', category: 'Aço', path: '/aco/base-rotulada' },
    { label: 'Flexão em Aço', category: 'Aço', path: '/aco/laminado-flexao' },
    { label: 'Compressão em Aço', category: 'Aço', path: '/aco/laminado-compressao' },
    { label: 'Aço formado a frio', category: 'Aço', path: '/aco/compressao-frio' },
    { label: 'Flexão em Aço Formado a Frio', category: 'Aço', path: '/aco/flexao-frio' },
    { label: 'Chapa de Piso em Aço', category: 'Aço', path: '/aco/chapa' },
    { label: 'Tubo Circular em Aço', category: 'Aço', path: '/aco/tubo-circular-flexao' },
    { label: 'Capacidade de Estacas', category: 'Geotecnia', path: '/geotecnia/estaca' },
    { label: 'Estabilidade de Muro de Arrimo', category: 'Geotecnia', path: '/geotecnia/muro-flexao' },
    { label: 'Circuitos Elétricos', category: 'Elétrica', path: '/eletrica/circuitos' },
    { label: 'Eletrodutos', category: 'Elétrica', path: '/eletrica/eletroduto' },
    { label: 'Fossa Séptica', category: 'Hidrossanitário', path: '/hidrossanitario/fossa' },
    { label: 'Reservatório', category: 'Hidrossanitário', path: '/hidrossanitario/reservatorio' },
    { label: 'Pluvial', category: 'Hidrossanitário', path: '/hidrossanitario/pluvial' },
    { label: 'Compressão em Alvenaria', category: 'Alvenaria', path: '/alvenaria/compressao' },
    { label: 'Flexão em Alvenaria', category: 'Alvenaria', path: '/alvenaria/flexao' },
    { label: 'Painel de Alvenaria', category: 'Alvenaria', path: '/alvenaria/painel' },
    { label: 'Cisalhamento em Alvenaria', category: 'Alvenaria', path: '/alvenaria/cisalhamento' },
    { label: 'Parede de Alvenaria', category: 'Alvenaria', path: '/alvenaria/parede' },
  ]

  const filteredResults = searchTerm.trim()
    ? quickSearchItems.filter((item) => {
        const query = searchTerm.toLowerCase()
        return (
          item.label.toLowerCase().includes(query) ||
          item.category.toLowerCase().includes(query) ||
          item.path.toLowerCase().includes(query)
        )
      })
    : quickSearchItems

  const openSearch = () => {
    setIsSearchOpen(true)
    setSearchTerm('')
    setIsOpen(false)
  }

  const closeSearch = () => setIsSearchOpen(false)

  return (
    <>
      <Container $isScrolled={isScrolled}>
        <Content>
          <Link to="/" onClick={closeMenu}>
            <Logo aria-label="Calculadoras de Engenharia">ce</Logo>
          </Link>
          <Link to="/" onClick={closeMenu} className="title-link">
            <h3>Calculadoras de Engenharia</h3>
          </Link>

          <DesktopLinks>
            <NavLink to="/" $active={isActive('/')}>
              Home
            </NavLink>
            <div className="dropdown">
              <span>Estruturas</span>
              <div className="dropdown-content">
                <DropdownItem to="/concreto">
                  <span className="icon">🏗️</span>
                  Concreto
                </DropdownItem>
                <DropdownItem to="/alvenaria">
                  <span className="icon">🧱</span>
                  Alvenaria
                </DropdownItem>
                <DropdownItem to="/aco">
                  <span className="icon">⚙️</span>
                  Aço
                </DropdownItem>
                <DropdownItem to="/geotecnia">
                  <span className="icon">🏔️</span>
                  Geotecnia
                </DropdownItem>
              </div>
            </div>
            <div className="dropdown">
              <span>Instalações</span>
              <div className="dropdown-content">
                <DropdownItem to="/hidrossanitario">
                  <span className="icon">💧</span>
                  Hidrossanitário
                </DropdownItem>
                <DropdownItem to="/eletrica">
                  <span className="icon">⚡</span>
                  Elétrica
                </DropdownItem>
              </div>
            </div>
            <ThemeToggle type="button" onClick={onToggleTheme} aria-pressed={isDark}>
              {isDark ? <FiSun /> : <FiMoon />}
              <span>{isDark ? 'Claro' : 'Escuro'}</span>
            </ThemeToggle>

            {isAuthenticated ? (
              <UserMenuContainer ref={userMenuRef}>
                <UsageBadge $variant={getBadgeVariant()}>{getBadgeText()}</UsageBadge>
                <UserMenuButton onClick={toggleUserMenu}>
                  <UserAvatar>{getUserInitials()}</UserAvatar>
                  <FiChevronDown />
                </UserMenuButton>
                {isUserMenuOpen && (
                  <UserDropdown>
                    <UserDropdownHeader>
                      <div className="email">{user?.email}</div>
                    </UserDropdownHeader>
                    {user?.isAdmin && (
                      <UserDropdownItem as={Link} to="/admin" onClick={() => setIsUserMenuOpen(false)}>
                        <FiShield />
                        Painel do Admin
                      </UserDropdownItem>
                    )}
                    {user?.subscriptionStatus === 'active' ? (
                      <UserDropdownItem onClick={handleManageSubscription}>
                        <FiSettings />
                        Gerenciar Assinatura
                      </UserDropdownItem>
                    ) : (
                      <UserDropdownItem as={Link} to="/pricing" onClick={() => setIsUserMenuOpen(false)}>
                        <FiCreditCard />
                        Ver Planos
                      </UserDropdownItem>
                    )}
                    <UserDropdownItem onClick={handleLogout} className="danger">
                      <FiLogOut />
                      Sair
                    </UserDropdownItem>
                  </UserDropdown>
                )}
              </UserMenuContainer>
            ) : (
              <AuthButtons>
                <AuthButton to="/login" className="login">
                  Entrar
                </AuthButton>
                <AuthButton to="/register" className="register">
                  Criar conta
                </AuthButton>
              </AuthButtons>
            )}
          </DesktopLinks>

          <NavMenu $isOpen={isOpen}>
            <ThemeToggleMobile type="button" onClick={onToggleTheme} aria-pressed={isDark}>
              {isDark ? <FiSun /> : <FiMoon />}
              <span>Modo {isDark ? 'claro' : 'escuro'}</span>
            </ThemeToggleMobile>
            <Link to="/" onClick={closeMenu}>
              Home
            </Link>
            <strong>Estruturas</strong>
            <Link to="/concreto" onClick={closeMenu}>
              🏗️ Concreto
            </Link>
            <Link to="/alvenaria" onClick={closeMenu}>
              🧱 Alvenaria
            </Link>
            <Link to="/aco" onClick={closeMenu}>
              ⚙️ Aço
            </Link>
            <Link to="/geotecnia" onClick={closeMenu}>
              🏔️ Geotecnia
            </Link>
            <strong>Instalações</strong>
            <Link to="/hidrossanitario" onClick={closeMenu}>
              💧 Hidrossanitário
            </Link>
            <Link to="/eletrica" onClick={closeMenu}>
              ⚡ Elétrica
            </Link>

            {isAuthenticated ? (
              <>
                <strong style={{ marginTop: '20px' }}>Conta</strong>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 0',
                    borderBottom: '1px solid var(--border)',
                    marginBottom: '8px',
                  }}
                >
                  <UserAvatar>{getUserInitials()}</UserAvatar>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', wordBreak: 'break-all' }}>
                      {user?.email}
                    </div>
                    <div style={{ marginTop: '6px' }}>
                      <UsageBadge $variant={getBadgeVariant()}>{getBadgeText()}</UsageBadge>
                    </div>
                  </div>
                </div>
                {user?.isAdmin && (
                  <Link to="/admin" onClick={closeMenu}>
                    🛡️ Painel do Admin
                  </Link>
                )}
                {user?.subscriptionStatus === 'active' ? (
                  <Link
                    to="#"
                    onClick={(e) => {
                      e.preventDefault()
                      closeMenu()
                      handleManageSubscription()
                    }}
                  >
                    ⚙️ Gerenciar Assinatura
                  </Link>
                ) : (
                  <Link to="/pricing" onClick={closeMenu}>
                    💳 Ver Planos
                  </Link>
                )}
                <Link to="/" onClick={handleLogout} style={{ color: '#ef4444', fontWeight: '600' }}>
                  🚪 Sair
                </Link>
              </>
            ) : (
              <>
                <strong style={{ marginTop: '20px' }}>Autenticação</strong>
                <Link to="/pricing" onClick={closeMenu}>
                  💎 Ver Planos
                </Link>
                <Link to="/login" onClick={closeMenu}>
                  🔑 Entrar
                </Link>
                <Link to="/register" onClick={closeMenu}>
                  ✨ Criar conta grátis
                </Link>
              </>
            )}
          </NavMenu>

          <MenuIcon onClick={() => setIsOpen(!isOpen)} $isOpen={isOpen}>
            {isOpen ? <FiX /> : <FiMenu />}
          </MenuIcon>
        </Content>
      </Container>
      <BottomNav>
        {bottomItems.map((item) => {
          const Icon = item.icon
          if (item.action === 'search') {
            return (
              <BottomNavItem as="button" type="button" key={item.label} onClick={openSearch} $active={isSearchOpen}>
                <Icon />
                <span>{item.label}</span>
              </BottomNavItem>
            )
          }
          return (
            <BottomNavItem key={item.label} to={item.to} $active={isActive(item.to)}>
              <Icon />
              <span>{item.label}</span>
            </BottomNavItem>
          )
        })}
      </BottomNav>

      {isSearchOpen && (
        <SearchOverlay onClick={closeSearch}>
          <SearchPanel onClick={(event) => event.stopPropagation()}>
            <SearchHeader>
              <h3>Buscar calculadora</h3>
              <SearchClose type="button" onClick={closeSearch} aria-label="Fechar busca">
                <FiX />
              </SearchClose>
            </SearchHeader>
            <SearchInput
              autoFocus
              placeholder="Ex.: Flexão em concreto, estacas, solda..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            <SearchResults>
              {filteredResults.map((item) => (
                <SearchResultItem key={item.path} to={item.path} onClick={closeSearch}>
                  <strong>{item.label}</strong>
                  <span>{item.category}</span>
                </SearchResultItem>
              ))}
              {filteredResults.length === 0 && <span>Nenhum resultado encontrado.</span>}
            </SearchResults>
          </SearchPanel>
        </SearchOverlay>
      )}

      <MenuOverlay $isOpen={isOpen} onClick={closeMenu} />
    </>
  )
}
